import type { ReactNode } from 'react';

import { buildSegmentPaths } from '../../lib/multi-segment-donut-paths';
import { cn } from '../../lib/utils';

export interface MultiSegmentDonutSegment {
  id: string;
  /** 0–100; clamped collectively. */
  percent: number;
  color: string;
  /**
   * Defaults to true. False drops the pointer cursor and the hit area with the
   * click, so a segment that leads nowhere does not advertise that it does.
   */
  selectable?: boolean;
}

/** grey-200, the same track the single-value `Donut` draws behind its arc. */
const DEFAULT_TRACK_COLOR = '#D9D9D9';

export interface MultiSegmentDonutProps {
  segments: readonly MultiSegmentDonutSegment[];
  size?: number;
  strokeWidth?: number;
  /** Background ring drawn behind the segments. Defaults to grey-200. */
  trackColor?: string;
  /**
   * Default scalar value rendered in the center when no `centerSlot` is provided.
   * If both `value` and `centerSlot` are supplied, `centerSlot` wins and a
   * dev-mode `console.warn` is emitted (see design.md decision log #5 / Risk #5).
   */
  value?: string | number;
  /**
   * Rendered absolutely centered inside the ring. Takes precedence over `value`
   * when both are provided (dev-mode `console.warn` is emitted).
   */
  centerSlot?: ReactNode;
  /** Fires when a segment is clicked. */
  onSegmentClick?: (segmentId: string) => void;
  className?: string;
}

export function MultiSegmentDonut({
  segments,
  size = 240,
  strokeWidth = 24,
  trackColor = DEFAULT_TRACK_COLOR,
  value,
  centerSlot,
  onSegmentClick,
  className,
}: MultiSegmentDonutProps): JSX.Element {
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;

  const paths = buildSegmentPaths({ size, strokeWidth, segments });

  const unselectable = new Set(
    segments.filter((segment) => segment.selectable === false).map((segment) => segment.id)
  );
  const isSelectable = (id: string): boolean =>
    onSegmentClick !== undefined && !unselectable.has(id);

  // Dev-mode precedence warning: if a consumer supplies BOTH value and centerSlot,
  // centerSlot wins. design.md decision log #5 / Risk #5.
  if (process.env.NODE_ENV !== 'production' && value !== undefined && centerSlot !== undefined) {
    // eslint-disable-next-line no-console
    console.warn('MultiSegmentDonut: centerSlot takes precedence over value');
  }

  const resolvedCenter: ReactNode =
    centerSlot !== undefined ? centerSlot : value !== undefined ? value : null;

  return (
    <div
      className={cn('relative inline-flex items-center justify-center shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 -rotate-90"
        aria-hidden="true"
      >
        {/*
         * Drawn whatever the segments say. The segments cover only the part of
         * the scope that resolved to a tier, so without a track the remainder
         * is not "the rest" but a hole — and a scope with nothing in any tier
         * renders no ring at all, which reads as broken rather than empty.
         */}
        <circle
          data-testid="donut-track"
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {paths.map((path) => (
          <circle
            key={path.id}
            data-tier-id={path.id}
            data-testid="donut-slice"
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={path.color}
            strokeWidth={strokeWidth}
            strokeDasharray={path.strokeDasharray}
            strokeDashoffset={path.strokeDashoffset}
            style={{
              pointerEvents: isSelectable(path.id) ? 'stroke' : 'none',
              cursor: isSelectable(path.id) ? 'pointer' : undefined,
            }}
            onClick={isSelectable(path.id) ? () => onSegmentClick?.(path.id) : undefined}
          />
        ))}
      </svg>
      {resolvedCenter !== null ? (
        <div className="relative flex flex-col items-center justify-center z-10">
          {resolvedCenter}
        </div>
      ) : null}
    </div>
  );
}

MultiSegmentDonut.displayName = 'MultiSegmentDonut';
